import { Delete, Edit, SmartToy } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import { blogService, BlogFilters } from "../../../api/services/blogService";

const BlogList = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState<BlogFilters>({
    status: undefined,
    category: "",
    search: "",
    limit: 10,
    offset: 0,
  });

  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);

  const { data, isLoading } = useQuery(
    ["blogs", { ...filters, limit, offset: page * limit }],
    () => blogService.getBlogs({ ...filters, limit, offset: page * limit }),
    {
      keepPreviousData: true,
    }
  );

  const deleteMutation = useMutation(
    (id: number) => blogService.deleteBlog(id),
    {
      onSuccess: () => {
        toast.success("Article deleted successfully");
        queryClient.invalidateQueries("blogs");
      },
      onError: () => {
        toast.error("Failed to delete article");
      },
    }
  );

  const publishMutation = useMutation(
    (id: number) => blogService.publishBlog(id),
    {
      onSuccess: () => {
        toast.success("Article published successfully");
        queryClient.invalidateQueries("blogs");
      },
      onError: () => {
        toast.error("Failed to publish article");
      },
    }
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case "PUBLISHED":
        return "success";
      case "DRAFT":
        return "warning";
      case "ARCHIVED":
        return "default";
      default:
        return "default";
    }
  };

  const handleDelete = (id: number) => {
    if (window.confirm("Are you sure you want to delete this article?")) {
      deleteMutation.mutate(id);
    }
  };

  const handleStatusFilter = (value: any) => {
    setFilters({ ...filters, status: value || undefined });
    setPage(0);
  };

  const handleSearch = (value: string) => {
    setFilters({ ...filters, search: value });
    setPage(0);
  };

  if (isLoading && !data) {
    return <Loader />;
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">Blog Articles</Typography>
        <Button variant="contained" onClick={() => navigate("/blog/new")}>
          + Create Article
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <TextField
          label="Search"
          placeholder="Search by title, content..."
          value={filters.search}
          onChange={(e) => handleSearch(e.target.value)}
          sx={{ flex: 1 }}
          size="small"
        />
        <FormControl sx={{ minWidth: 200 }} size="small">
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status || ""}
            onChange={(e) => handleStatusFilter(e.target.value)}
            label="Status"
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="DRAFT">Draft</MenuItem>
            <MenuItem value="PUBLISHED">Published</MenuItem>
            <MenuItem value="ARCHIVED">Archived</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Title</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Views</TableCell>
            <TableCell>Author</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data?.data?.articles?.map((article) => (
            <TableRow key={article.id}>
              <TableCell>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {article.title}
                  </Typography>
                  <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                    {article.is_featured && (
                      <Chip label="Featured" size="small" color="primary" />
                    )}
                    {article.is_ai_generated && (
                      <Chip
                        icon={<SmartToy sx={{ fontSize: 14 }} />}
                        label="AI"
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>
              </TableCell>
              <TableCell>
                <Chip
                  label={article.status}
                  color={getStatusColor(article.status)}
                  size="small"
                />
              </TableCell>
              <TableCell>{article.category || "-"}</TableCell>
              <TableCell>{article.view_count}</TableCell>
              <TableCell>{article.author_name || "-"}</TableCell>
              <TableCell>
                {new Date(article.created_at).toLocaleDateString()}
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <IconButton
                    size="small"
                    onClick={() => navigate(`/blog/edit/${article.id}`)}
                  >
                    <Edit />
                  </IconButton>
                  {article.status === "DRAFT" && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => publishMutation.mutate(article.id)}
                    >
                      Publish
                    </Button>
                  )}
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDelete(article.id)}
                  >
                    <Delete />
                  </IconButton>
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <TablePagination
        component="div"
        count={data?.data?.count || 0}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={limit}
        onRowsPerPageChange={(e) => {
          setLimit(parseInt(e.target.value, 10));
          setPage(0);
        }}
      />
    </Box>
  );
};

export default BlogList;
